import { useState, useEffect, useCallback } from 'react';
import { enteteAuthPublique } from '@/lib/suiviPublic';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/**
 * Hook partagé pour le chargement des pages publiques avec retry automatique.
 *
 * @param {string} path - Chemin API relatif (ex: `/api/public/soumission/${token}`)
 * @param {object} options
 * @param {number}  options.retries       - Nombre de tentatives (défaut 3)
 * @param {number}  options.retryDelay    - Délai entre les retries en ms (défaut 2500)
 * @param {string}  options.fallbackPath  - Chemin API de secours (HTML statique)
 * @param {string}  options.notFoundMsg   - Message pour 404
 * @param {string}  options.genericErrorMsg - Message générique
 *
 * @returns {{ data, loading, error, refetch }}
 */
export function usePublicFetch(path, options = {}) {
  const {
    retries = 3,
    retryDelay = 2500,
    fallbackPath = null,
    notFoundMsg = "Ce document n'existe pas ou le lien est invalide.",
    genericErrorMsg = 'Erreur lors du chargement.',
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tryFallback = async () => {
    if (!fallbackPath) return false;
    try {
      const res = await fetch(`${API_URL}${fallbackPath}`);
      if (res.ok) {
        setData({ _fallbackHtml: await res.text() });
        return true;
      }
    } catch (e) { console.error("Error:", e); }
    return false;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);

    for (let i = 0; i < retries; i++) {
      try {
        const res = await fetch(`${API_URL}${path}`, { headers: enteteAuthPublique() });  // 📬 employé connecté → ouverture non comptée

        if (res.ok) {
          setData(await res.json());
          setLoading(false);
          return;
        }

        // 🧱 Phase 4 — Lot C (D3) : 410 = lien expiré/sans jeton → message FR-CA du backend, pas de retry
        if (res.status === 410) {
          let detail = "Ce lien a expiré — demandez un nouveau lien.";
          try { const d = (await res.json())?.detail; if (d) detail = d; } catch { /* corps vide */ }  // objet {code, message} conservé (lien_remplace)
          setError(detail);
          setLoading(false);
          return;
        }

        // Retry sur 404 ou 500+ sauf dernier essai
        if ((res.status === 404 || res.status >= 500) && i < retries - 1) {
          await new Promise(r => setTimeout(r, res.status === 404 ? 2000 : retryDelay));
          continue;
        }

        // Dernier essai : tenter le fallback
        if (await tryFallback()) { setLoading(false); return; }

        // 🚨 Erreur HONNÊTE : 404 → introuvable ; 410 → retirée / lien remplacé (detail serveur) ; 5xx → technique (Réessayer)
        if (res.status === 404) setError({ code: 'introuvable', message: notFoundMsg });
        else if (res.status === 410) {
          let d = null; try { d = (await res.json())?.detail; } catch { /* corps vide */ }
          setError(typeof d === 'object' && d ? d : { code: 'retiree', message: typeof d === 'string' ? d : 'Ce document a été retiré — contactez-nous.' });
        } else setError({ code: 'technique', message: "Un problème technique temporaire empêche l'affichage — réessayez dans quelques instants." });
        setLoading(false);
        return;
      } catch {
        if (i < retries - 1) {
          await new Promise(r => setTimeout(r, retryDelay));
          continue;
        }
        if (await tryFallback()) { setLoading(false); return; }
        setError({ code: 'technique', message: "Un problème technique temporaire empêche l'affichage — réessayez dans quelques instants." });
        setLoading(false);
      }
    }
  }, [path, retries, retryDelay, fallbackPath, notFoundMsg, genericErrorMsg]);

  useEffect(() => {
    if (path) fetchData();
  }, [path, fetchData]);

  return { data, loading, error, refetch: fetchData };
}
